import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PRODUCT_IDS } from "@/content/products";
import { ROUTES } from "@/content/routes";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { Container, Eyebrow, Mono, SectionTitle } from "@/components/site/primitives";
import { cn } from "@/lib/utils";

export const ProductTop = ({ id }) => (
  <Container className="pt-8">
    <div className="flex flex-col gap-4 border-b border-o-line pb-4 md:flex-row md:items-center md:justify-between">
      <Breadcrumbs routeKey={id} />
      <nav aria-label="Products" className="flex flex-wrap items-center gap-1" data-testid="product-sibling-nav">
        {PRODUCT_IDS.map((p) => (
          <Link key={p} to={ROUTES[p].path} data-testid={`product-nav-${p}`} aria-current={p === id ? "page" : undefined}
            className={cn("px-3 py-1.5 text-sm transition-colors", p === id ? "bg-o-text text-o-bg" : "text-o-dim hover:text-o-text")}>
            {ROUTES[p].label}
          </Link>
        ))}
        <Link to="/research#formation" data-testid="product-nav-research"
          className="ml-2 border-l border-o-line pl-3 font-mono text-[10.5px] uppercase tracking-[0.16em] text-o-dim transition-colors hover:text-o-accent">
          Shared research foundation
        </Link>
      </nav>
    </div>
  </Container>
);

const FoundationLink = ({ to, title, text, testid }) => (
  <Link to={to} data-testid={testid} className="group flex items-start justify-between gap-4 border border-o-line bg-o-surface p-5 transition-colors hover:border-o-accent">
    <span>
      <span className="block font-display text-lg font-bold leading-tight">{title}</span>
      <span className="mt-1 block text-sm text-o-dim">{text}</span>
    </span>
    <ArrowUpRight className="h-4 w-4 shrink-0 text-o-dim group-hover:text-o-accent" />
  </Link>
);

export const ProductFoundation = ({ product }) => (
  <section className="border-t border-o-line" data-testid="product-foundation">
    <Container className="grid gap-10 py-16 lg:grid-cols-12 lg:py-24">
      <div className="lg:col-span-5">
        <Eyebrow>Shared research foundation</Eyebrow>
        <SectionTitle className="mt-5 text-3xl sm:text-4xl lg:text-4xl">Where {product.name} comes from.</SectionTitle>
        <p className="mt-4 leading-relaxed text-o-dim">
          {product.name} was originated within Research & Products. It shares its methods with the other products, but takes the form its own human setting calls for.
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:col-span-7">
        <FoundationLink to="/research#formation" testid="foundation-formation" title="Product formation" text="Research → Formation → Prototype → Product → Learning" />
        <FoundationLink to="/research#human-structure" testid="foundation-human-structure" title="Human Structure" text="The shared intellectual foundation" />
        <FoundationLink to="/ordo" testid="foundation-general-ordo" title="General Ordo" text="The governing architecture" />
        <div className="border border-dashed border-o-strong p-5" data-testid="product-architecture-slot">
          <Mono>Underlying architectural relationships</Mono>
          <p className="mt-2 text-sm text-o-dim">The specific relationships between {product.name} and the shared architecture will be described in Phase 3.</p>
        </div>
      </div>
    </Container>
  </section>
);
